import type { StatusLevel, WaterfallSpan } from "../../components/index.ts";
import type { Span, SpanStatus, TraceDetail, TraceSummary } from "./use-traces.ts";

/**
 * Presentation vocabulary for the Traces screen (task 11.11; design 12.2 Traces row, 12.3/12.4
 * principles). It keeps the one-way maps from the generated contract shapes onto the screen's
 * presentational pieces in a single, testable place and defines NO API shapes (those come from
 * `@oathera/contract/api` via `use-traces.ts`).
 *
 * THE INDEPENDENT inferred/partial RULE (design 7.4 / 12.2; the named, testable requirement). A
 * trace — and, where the contract carries it, a span — may bear two labels that the contract and
 * the design both state are INDEPENDENT:
 *   - `inferred`: the trace was correlated by `decision_id` / agent / time because no `traceparent`
 *     header could propagate (FR-8.2);
 *   - `partial`: allow-path spans were sampled out while a deny/error span was kept (FR-8.3).
 * They are two independent booleans. A trace is in exactly one of FOUR combinations — neither, only
 * inferred, only partial, OR BOTH — and this screen renders each combination faithfully:
 *   - the two flags are read SEPARATELY off the row ({@link traceLabels}) and rendered as two
 *     SEPARATE badges;
 *   - when both are true, BOTH badges show;
 *   - NEITHER takes precedence: they are not collapsed into one combined label, one is never hidden
 *     when the other is present, and they are not ordered so one dominates (a stable, fixed order —
 *     inferred then partial — is used only for consistent reading, carrying no precedence).
 * {@link traceLabels} is the single, testable place that computes this, and the shared `Waterfall`
 * component (task 11.2) draws the same two-independent-badge rule for the detail waterfall.
 *
 * The span status (`SpanStatus` = ok | error | denied) maps to the icon+text `StatusBadge`
 * (design 12.4 "status by icon+text, never colour alone"); an unverifiable/unknown status is never
 * green (design 12.3). The maps here are also the single place a span becomes an `/events` standing
 * filter (design 12.3 drill-downs: a span → its events) and a trace summary becomes the set of
 * independent-badge props the list and the Waterfall consume.
 */

/* ---- The independent inferred/partial labels (the named requirement) ------------------------ */

/** The two independent trace labels, each INDEPENDENTLY present or absent. */
export interface TraceLabelState {
  /** The trace/span was correlated by inference (no propagated trace header). */
  readonly inferred: boolean;
  /** The trace/span is missing spans that were sampled out. */
  readonly partial: boolean;
}

/**
 * Read the two INDEPENDENT labels off a trace (or anything carrying both booleans). This is the one
 * place the rule lives: the two flags are copied straight across with NO interaction — one is never
 * derived from, suppressed by, or merged with the other. The result preserves all four combinations
 * (none / inferred-only / partial-only / both) exactly as the contract reported them, so the list
 * and the waterfall can render each independent badge on its own merits with neither taking
 * precedence.
 */
export function traceLabels(trace: Pick<TraceSummary, "inferred" | "partial">): TraceLabelState {
  return { inferred: trace.inferred, partial: trace.partial };
}

/** The fixed, precedence-free reading order of the labels (consistency only, not dominance). */
export const TRACE_LABEL_ORDER: readonly (keyof TraceLabelState)[] = ["inferred", "partial"];

/** Human text for a label. */
export function traceLabelText(kind: keyof TraceLabelState): string {
  return kind === "inferred" ? "inferred" : "partial";
}

/** The neutral glyph for a label (icon+text, never a status colour — design 12.4). */
export function traceLabelGlyph(kind: keyof TraceLabelState): string {
  return kind === "inferred" ? "≈" : "⋯";
}

/** A longer accessible phrase for a label, used as its `title`. */
export function traceLabelTitle(kind: keyof TraceLabelState): string {
  return kind === "inferred"
    ? "Inferred: correlated without a propagated trace header (decision id, agent and time)."
    : "Partial: some spans were sampled out, so the trace is incomplete.";
}

/* ---- Span status: the non-colour conveyance (design 12.4) ----------------------------------- */

/**
 * Map a span status onto a status level for the icon+text `StatusBadge`. `error` and `denied` are
 * both `error`-level (a denied call and a failed call are each a non-success outcome the operator
 * must see), distinguished by the LABEL text below, not by colour. `ok` is `neutral` — a span that
 * ran is not a reassuring health "green" (design 12.3: a trace span is a fact, not a health state),
 * so no span status ever maps to `healthy`.
 */
export function spanStatusLevel(status: SpanStatus): StatusLevel {
  switch (status) {
    case "ok":
      return "neutral";
    case "denied":
      return "warn";
    case "error":
      return "error";
  }
}

/** Human label for a span status. */
export function spanStatusLabel(status: SpanStatus): string {
  switch (status) {
    case "ok":
      return "OK";
    case "denied":
      return "Denied";
    case "error":
      return "Error";
  }
}

/** A longer accessible phrase for a span/trace status, used as a badge `title`. */
export function spanStatusTitle(status: SpanStatus): string {
  switch (status) {
    case "ok":
      return "Worst span status: ok.";
    case "denied":
      return "Worst span status: a span was denied.";
    case "error":
      return "Worst span status: a span errored.";
  }
}

/** Human label for a span kind (`server | client | internal`). */
export function spanKindLabel(kind: Span["kind"]): string {
  switch (kind) {
    case "server":
      return "server";
    case "client":
      return "client";
    case "internal":
      return "internal";
  }
}

/* ---- Trace → Waterfall span mapping --------------------------------------------------------- */

/**
 * Whether the contract carries PER-SPAN `inferred`/`partial` flags. It does NOT: the contract `Span`
 * schema has only span_id, parent_span_id, service, name, kind, start_ts, duration_ms and status —
 * no span-level `inferred`/`partial`. Those two labels exist in the contract ONLY at the trace level
 * (`TraceSummary`/`TraceDetail`). The shared `Waterfall` component CAN render per-span badges, but
 * the honest answer here is that there is no per-span flag to feed it, so span-level badges are left
 * unset rather than fabricated from the trace-level flags (design 12.3: surface the gap honestly,
 * do not invent). The trace-level `inferred`/`partial` are mapped faithfully to the Waterfall's
 * trace-level props (see {@link toWaterfall}).
 */
export const SPAN_LEVEL_LABELS_AVAILABLE = false as const;

/**
 * Map one contract {@link Span} onto a {@link WaterfallSpan}, given the trace start (so offsets are
 * relative to the trace, not the epoch) and the computed nesting depth. The span's `start_ts` minus
 * the trace start gives its offset; its `duration_ms` is carried through; its status becomes the
 * icon+text level. Per-span `inferred`/`partial` are deliberately LEFT UNSET
 * ({@link SPAN_LEVEL_LABELS_AVAILABLE} is false) — the contract carries no span-level flag, so none
 * is invented.
 */
export function toWaterfallSpan(span: Span, traceStartMs: number, depth: number): WaterfallSpan {
  const startMs = Math.max(0, span.start_ts - traceStartMs);
  return {
    id: span.span_id,
    name: span.name,
    service: span.service,
    startMs,
    durationMs: span.duration_ms,
    depth,
    status: spanStatusLevel(span.status),
    // No span-level inferred/partial in the contract (SPAN_LEVEL_LABELS_AVAILABLE === false).
  };
}

/**
 * Compute the nesting depth of each span from its `parent_span_id` chain. A root span
 * (`parent_span_id === null`, or a parent not present in the trace) is depth 0; every other span is
 * one deeper than its parent. The walk is cycle-safe (a span whose ancestry loops is capped at the
 * span count) and order-independent (depths are memoised), so a trace whose spans arrive in any
 * order still indents correctly.
 */
export function spanDepths(spans: readonly Span[]): ReadonlyMap<string, number> {
  const byId = new Map<string, Span>();
  for (const s of spans) byId.set(s.span_id, s);

  const depths = new Map<string, number>();
  const compute = (id: string, guard: number): number => {
    const cached = depths.get(id);
    if (cached !== undefined) return cached;
    const span = byId.get(id);
    // Missing span, no parent, or a parent outside this trace → root. Guard caps any cycle.
    if (span === undefined || span.parent_span_id === null || !byId.has(span.parent_span_id) || guard <= 0) {
      depths.set(id, 0);
      return 0;
    }
    const depth = compute(span.parent_span_id, guard - 1) + 1;
    depths.set(id, depth);
    return depth;
  };

  for (const s of spans) compute(s.span_id, spans.length);
  return depths;
}

/**
 * Build the full set of {@link WaterfallSpan}s for a trace, ordered for display: by start offset,
 * then by depth, then by name, so parents read before their children at the same start and the
 * waterfall reads top-to-bottom in time. The trace start is the earliest span `start_ts` (or the
 * trace's `started_at` when there are no spans). Depths come from {@link spanDepths}.
 */
export function toWaterfallSpans(detail: TraceDetail): readonly WaterfallSpan[] {
  const spans = detail.spans;
  if (spans.length === 0) return [];

  const starts = spans.map((s) => s.start_ts);
  const traceStartMs = Math.min(...starts);
  const depths = spanDepths(spans);

  const rows = spans.map((s) =>
    toWaterfallSpan(s, traceStartMs, depths.get(s.span_id) ?? 0),
  );

  return [...rows].sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    const ad = a.depth ?? 0;
    const bd = b.depth ?? 0;
    if (ad !== bd) return ad - bd;
    return a.name.localeCompare(b.name);
  });
}

/* ---- Emptiness ------------------------------------------------------------------------------- */

/** True when the trace list has no traces to show. */
export function isTracesEmpty(page: { items: readonly TraceSummary[] }): boolean {
  return page.items.length === 0;
}

/** True when a trace detail has no spans to draw. */
export function isTraceSpansEmpty(detail: TraceDetail): boolean {
  return detail.spans.length === 0;
}

/* ---- Drill-down to /events ------------------------------------------------------------------- */

/**
 * Build a relative `/events` link from a set of filter params. Values are encoded by
 * `URLSearchParams`; empty values are dropped. The traces endpoints attach no server-built `drill`
 * (the responses carry no `drill` field), so the screen composes honest standing filters from real
 * `/events` params — never an invented route — consistent with the enforcement/posture screens. The
 * events screen (task 11.12) reads these params; `trace` is one of its standing filters.
 */
function eventsHref(params: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.length > 0) search.set(key, value);
  }
  const qs = search.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/** The `/events` filter for all events of a trace (the trace-level "see events" drill). */
export function traceEventsHref(traceId: string): string {
  return eventsHref({ trace: traceId });
}

/**
 * The `/events` filter for a single SPAN's events (design 12.3: a span → its events). It carries the
 * trace id and the span's service as faithful standing filters, so the drill lands on exactly the
 * events for that span's service within the trace. (The `/events` contract has no `span` filter, so
 * the drill narrows by trace + service rather than inventing a span param.)
 */
export function spanEventsHref(traceId: string, span: Pick<Span, "service">): string {
  return eventsHref({ trace: traceId, component: span.service });
}

/** The in-app route for a trace's waterfall detail (`/traces/{id}`). */
export function traceDetailHref(traceId: string): string {
  return `/traces/${encodeURIComponent(traceId)}`;
}
